import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { matchPath, Redirect, Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import { LibraryItem } from "../../entities/LibraryItem";
import { LibraryItemService } from "../../services/LibraryItemService";
import { Button, IconButton, Snackbar } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useIsMobile } from "../../components/Mui3WidthProvider";
import { DesktopLibraryLayout } from "./Layouts/DesktopLibraryLayout";
import { MobileLibraryLayout } from "./Layouts/MobileLibraryLayout";
import { routes } from "../../routes";
import { useQueryParams } from "../../components/useQueryParams";
import { LibraryOffline } from "./LibraryOffline";
import { LibraryStarred } from "./LibraryStarred";
import { LibrarySearch } from "./LibrarySearch";
import { LibraryHome } from "./LibraryHome";
import { LibraryRecent } from "./LibraryRecent";
import { PremiumSeries } from "./PremiumSeries";
import { PremiumSermonSeriesDetails } from "./PremiumSeriesDetails";
import { SermonTemplates } from "./SermonTemplates";
import { PremiumSermons } from "./PremiumSermons";
import { PremiumSermonDetails } from "./PremiumSermonDetails";
import { LibraryCalendar } from "./LibraryCalendar";
import { LoadingPage } from "../../components/LoadingPage";

export enum LibraryView {
	Home = 0,
	Recent = 1,
	Starred = 2,
	Search = 3,
	Calendar = 4,
	OfflineAccess = 5,
	SermonTemplateList = 6,
	PremiumSermonList = 7,
	PremiumSermonSeriesList = 8,
	PremiumSermonSeriesDetail = 9,
	PremiumSermonDetail = 10,
	SermonTemplateDetail = 11,
}

function getActiveLibraryViewFromPath(path: string): LibraryView {
	const options = { exact: true, strict: false };
	if(matchPath(path, { path: routes.app.libraryHomePage, ...options })) return LibraryView.Home;
	if(matchPath(path, { path: routes.app.libraryFolder, ...options })) return LibraryView.Home;
	if(matchPath(path, { path: routes.app.libraryRecentPage, ...options })) return LibraryView.Recent;
	if(matchPath(path, { path: routes.app.libraryStarredPage, ...options })) return LibraryView.Starred;
	if(matchPath(path, { path: routes.app.librarySearchPage, ...options })) return LibraryView.Search;
	if(matchPath(path, { path: routes.app.libraryCalendarPage, ...options })) return LibraryView.Calendar;
	if(matchPath(path, { path: routes.app.libraryOfflinePage, ...options })) return LibraryView.OfflineAccess;
	if(matchPath(path, { path: routes.app.sermonTemplateListPage, ...options })) return LibraryView.SermonTemplateList;
	if(matchPath(path, { path: routes.app.sermonTemplateDetailPage, ...options })) return LibraryView.SermonTemplateDetail;
	if(matchPath(path, { path: routes.app.premiumSermonListPage, ...options })) return LibraryView.PremiumSermonList;
	if(matchPath(path, { path: routes.app.premiumSermonDetailPage, ...options })) return LibraryView.PremiumSermonDetail;
	if(matchPath(path, { path: routes.app.premiumSeriesListPage, ...options })) return LibraryView.PremiumSermonSeriesList;
	if(matchPath(path, { path: routes.app.premiumSeriesDetailPage, ...options })) return LibraryView.PremiumSermonSeriesDetail;
	return LibraryView.Home;
}

interface LibraryContext {
	libraryItems: LibraryItem[];
	focusedItem: LibraryItem | null;
	addLibraryItem: (item: LibraryItem) => void;
	updateLibraryItem: (item: LibraryItem) =>void;
	removeLibraryItem: (id: string) => void;
	activeFolderId: string | null;
	setFocusedItemId: (id: string | null) => void;
	activeView: LibraryView;
}

const LibraryReactContext = React.createContext<LibraryContext>({} as LibraryContext);

export function useLibrary() {
	return React.useContext(LibraryReactContext);
}

type LibraryAlert = (message: string, undoAction?: () => void) => void;

const LibraryAlertReactContext = React.createContext<LibraryAlert>(() => {});
export function useLibraryAlert() {
	return React.useContext(LibraryAlertReactContext);
}

interface LibrarySearchContext {
	searchValue: string;
	setSearchValue: (value: string) => void;
}
const LibrarySearchReactContext = React.createContext<LibrarySearchContext>({} as LibrarySearchContext);

export function useLibrarySearch() {
	return React.useContext(LibrarySearchReactContext);
}

export function LibraryPage() {
	const location = useLocation();
	const history = useHistory();
	const params = useParams<{ id?: string }>();

	const queryParams = useQueryParams();
	const searchValue = useMemo(() => queryParams.get("query") ?? "", [queryParams]);
	
	const [loading, setLoading] = React.useState(false);
	const [libraryItems, setLibraryItems] = React.useState<LibraryItem[]>([]);
	const [focusedItemId, setFocusedItemId] = React.useState<string | null>(null);
	const paramsId = useMemo(() => params.id ?? null, [params])
	const activeView = useMemo(() => getActiveLibraryViewFromPath(location.pathname), [location.pathname]);
	const focusedItem = useMemo(() => libraryItems.find(i => i.id === focusedItemId) ?? null, [focusedItemId, libraryItems])

	useEffect(() => {
		async function fetchData() {
			setLoading(true);
			const result = await LibraryItemService.getAll();
			setLoading(false);
			if (result.success) {
				setLibraryItems(result.data);
			}
		}
		fetchData();
	}, []);

	const addLibraryItem = (item: LibraryItem) => {
		setLibraryItems((libraryItems) => [...(libraryItems || []), item]);
	};

	const updateLibraryItem = (item: LibraryItem) => {
		setLibraryItems((libraryItems) => libraryItems?.map(li => li.id === item.id ? item : li))
	}

	const removeLibraryItem = (id: string) => setLibraryItems((libraryItems) => libraryItems?.filter((i) => i.id !== id));

	const setSearchValue = (value: string | null) => {
		switch(activeView){
			case LibraryView.Search: return history.replace(routes.app.resolve.librarySearchPage(value));
			case LibraryView.PremiumSermonSeriesDetail:
				return history.push(routes.app.resolve.premiumSeriesListPage(value));
			case LibraryView.PremiumSermonSeriesList:
				return history.replace(routes.app.resolve.premiumSeriesListPage(value));
			case LibraryView.PremiumSermonDetail:
				return history.push(routes.app.resolve.premiumSermonListPage(value));
			case LibraryView.PremiumSermonList:
				return history.replace(routes.app.resolve.premiumSermonListPage(value));
			case LibraryView.SermonTemplateDetail:
				return history.push(routes.app.resolve.sermonTemplateListPage(value));
			case LibraryView.SermonTemplateList:
				return history.replace(routes.app.resolve.sermonTemplateListPage(value));
			default:
				return history.push(routes.app.resolve.librarySearchPage(value));
		}
	}

	if (loading) return <LoadingPage />;

	return (
		<LibraryReactContext.Provider value={{ libraryItems, focusedItem, addLibraryItem, updateLibraryItem, removeLibraryItem, activeFolderId: paramsId, setFocusedItemId, activeView }}>
			<LibrarySearchReactContext.Provider value={{ searchValue, setSearchValue}}>
				<LibraryLayout>
					<Switch>
						<Route exact path={routes.app.premiumSeriesDetailPage}>
							<PremiumSermonSeriesDetails/>
						</Route>
						<Route exact path={routes.app.premiumSermonDetailPage}>
							<PremiumSermonDetails />
						</Route>
						<Route exact path={routes.app.libraryStarredPage}>
							<LibraryStarred />
						</Route>
						<Route exact path={routes.app.libraryOfflinePage}>
							<LibraryOffline />
						</Route>
						<Route exact path={routes.app.librarySearchPage}>
							<LibrarySearch />
						</Route>
						<Route exact path={routes.app.libraryRecentPage}>
							<LibraryRecent />
						</Route>
						<Route exact path={routes.app.premiumSeriesListPage}>
							<PremiumSeries />
						</Route>
						<Route exact path={routes.app.premiumSermonListPage}>
							<PremiumSermons />
						</Route>
						<Route exact path={routes.app.sermonTemplateListPage}>
							<SermonTemplates />
						</Route>
						<Route exact path={routes.app.libraryCalendarPage}>
							<LibraryCalendar />
						</Route>
						<Route exact path={[routes.app.libraryHomePage, routes.app.libraryFolder]}>
							<LibraryHome />
						</Route>
						<Redirect to={routes.app.libraryHomePage} />
					</Switch>
				</LibraryLayout>
			</LibrarySearchReactContext.Provider>
		</LibraryReactContext.Provider>
	);
}

function LibraryLayout(props: PropsWithChildren<{}>) {
	const isMobile = useIsMobile();
	const { children } = props;

	const [snackbarMessage, setSnackbarMessage] = React.useState<string | null>(null);
	const [snackbarUndoAction, setSnackbarUndoAction] = React.useState<(() => void) | undefined>();
	const [disableSnackbarActions, setDisableSnackbarActions] = React.useState(false);

	const onLibraryAlert: LibraryAlert = useMemo(
		() => (message: string, undoAction?: () => void) => {
			setSnackbarMessage(message);
			setSnackbarUndoAction(() => undoAction);
		},
		[]
	);

	const handleUndo = async () => {
		if (snackbarUndoAction) {
			setDisableSnackbarActions(true);
			await snackbarUndoAction();
			setDisableSnackbarActions(false);
		}
	};

	const layout = isMobile ? <MobileLibraryLayout>{children}</MobileLibraryLayout> : <DesktopLibraryLayout>{children}</DesktopLibraryLayout>;

	return (
		<LibraryAlertReactContext.Provider value={onLibraryAlert}>
			{layout}
			<Snackbar
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				open={snackbarMessage !== null}
				autoHideDuration={6000}
				onClose={() => setSnackbarMessage(null)}
				ContentProps={{
					"aria-describedby": "message-id",
				}}
				message={<span id="message-id">{snackbarMessage}</span>}
				action={[
					<span style={{ marginTop: "5px" }}>
						{snackbarUndoAction && (
							<Button
								color="primary"
								variant="outlined"
								onClick={handleUndo}
								style={{ marginRight: 5 }}
								disabled={disableSnackbarActions}
							>
								Undo
							</Button>
						)}
						<IconButton
							key="close"
							aria-label="Close"
							color="inherit"
							disabled={disableSnackbarActions}
							onClick={() => setSnackbarMessage(null)}
						>
							<CloseIcon />
						</IconButton>
					</span>,
				]}
			/>
		</LibraryAlertReactContext.Provider>
	);
}